import { useEffect, useMemo, useState } from 'react';
import { useTrip } from '../lib/tripContext.jsx';
import {
  pushFeatureState,
  getPermissionState,
  getCurrentSubscription,
  subscribePush,
  unsubscribePush,
  sendTestPush,
} from '../lib/push.js';
import SessionExpiredBanner from './SessionExpiredBanner.jsx';

// Push subscribe widget. Shows three states:
// 1. iOS Safari + not installed → render the Add-to-Home-Screen guide.
//    iOS Safari refuses to expose PushManager outside an installed PWA;
//    the user must install first.
// 2. Subscribable → button to enable + a tiny "send test" link.
// 3. Subscribed → confirmation + unsubscribe link.

export default function PushSubscribe({ compact = false }) {
  const { tripId } = useTrip();
  const feature = useMemo(() => pushFeatureState(), []);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const perm = await getPermissionState();
      const sub = await getCurrentSubscription().catch(() => null);
      if (cancelled) return;
      setPermission(perm);
      setSubscribed(!!sub);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function enable() {
    setError(null);
    setSessionExpired(false);
    setBusy('subscribe');
    try {
      await subscribePush({ tripId });
      setSubscribed(true);
      setPermission('granted');
      // Schedule the onboarding-intro push to fire 30 seconds after a
      // non-kris player completes subscription. Server-side gate
      // (isKrisExcluded + sentinel) is the source of truth — the
      // client just kicks the timer. Failure is silent: the
      // transmission is already seeded server-side at briefing-accept,
      // so the audio is reachable from the Wilman tab without push.
      window.setTimeout(() => {
        fetch('/api/onboarding-intro/push', {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ trip_id: tripId }),
        }).catch(() => {
          /* swallow — server will retry on next subscribe, audio is
             already in the transmissions log either way. */
        });
      }, 30_000);
    } catch (err) {
      if (err.status === 401) {
        setSessionExpired(true);
      } else {
        setError(err.message || String(err));
      }
    } finally {
      setBusy(null);
    }
  }

  async function disable() {
    setError(null);
    setBusy('unsubscribe');
    try {
      await unsubscribePush({ tripId });
      setSubscribed(false);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(null);
    }
  }

  async function test() {
    setError(null);
    setSessionExpired(false);
    setTestResult(null);
    setBusy('test');
    try {
      const r = await sendTestPush({ tripId });
      setTestResult(r);
    } catch (err) {
      if (err.status === 401) {
        setSessionExpired(true);
      } else {
        setError(err.message || String(err));
      }
    } finally {
      setBusy(null);
    }
  }

  if (feature.reason === 'ios-needs-install') {
    return <IOSInstallGuide compact={compact} />;
  }
  if (!feature.supported) {
    return (
      <div className="card px-4 py-3 text-sm text-fg-secondary">
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">push unavailable</p>
        <p className="mt-1">Your browser doesn&apos;t support web push ({feature.reason}).</p>
      </div>
    );
  }
  if (permission === 'denied') {
    return (
      <div className="card px-4 py-3 text-sm text-fg-secondary">
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">push blocked</p>
        <p className="mt-1">
          You denied notifications for this site. Re-enable in your browser&apos;s site settings, then return.
        </p>
      </div>
    );
  }

  return (
    <div className="card px-4 py-3 text-sm">
      <div className="flex items-baseline justify-between">
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          {subscribed ? "Wilman's transmissions · on" : "Wilman's transmissions"}
        </p>
        {feature.isIOS && (
          <span className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">iOS</span>
        )}
      </div>
      <p className="mt-1 font-serif text-fg-primary">
        {subscribed
          ? 'You will receive incoming transmissions, verdicts, and the daily brief on this device.'
          : 'Enable to receive incoming challenges and verdicts. Quiet hours 21:00–09:00 are enforced.'}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {!subscribed ? (
          <button
            type="button"
            onClick={enable}
            disabled={busy === 'subscribe'}
            className="rounded-md border border-action bg-action px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-action-fg transition hover:bg-action-hover disabled:opacity-60"
          >
            {busy === 'subscribe' ? 'enabling…' : 'enable transmissions'}
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={test}
              disabled={busy === 'test'}
              className="rounded-md border border-border-tertiary bg-bg-secondary px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-fg-secondary transition hover:bg-bg-tertiary disabled:opacity-60"
            >
              {busy === 'test' ? 'sending…' : 'send test push'}
            </button>
            <button
              type="button"
              onClick={disable}
              disabled={busy === 'unsubscribe'}
              className="rounded-md border border-border-tertiary bg-transparent px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary transition hover:bg-bg-tertiary disabled:opacity-60"
            >
              {busy === 'unsubscribe' ? 'disabling…' : 'disable'}
            </button>
          </>
        )}
      </div>
      {testResult && (
        <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          test sent · {testResult.sent} delivered{testResult.removed ? ` · ${testResult.removed} dead removed` : ''}
        </p>
      )}
      {sessionExpired && (
        <div className="mt-2">
          <SessionExpiredBanner compact />
        </div>
      )}
      {error && !sessionExpired && (
        <p className="mt-2 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

function IOSInstallGuide({ compact }) {
  return (
    <div className="card px-4 py-4 text-sm">
      <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
        iOS · install required
      </p>
      <p className="mt-1 font-serif text-fg-primary">
        Apple gates web push behind &ldquo;Add to Home Screen.&rdquo; One-time setup:
      </p>
      <ol className="mt-2 space-y-1.5 text-fg-secondary">
        <li>
          1. Tap the <span className="font-mono">share</span> icon in Safari (square with up-arrow).
        </li>
        <li>
          2. Scroll and tap <span className="font-mono">Add to Home Screen</span>.
        </li>
        <li>3. Confirm. The Some Say icon lands on your home screen.</li>
        <li>
          4. <strong>Open the app from the home-screen icon</strong> (not Safari) and return to this
          panel — push will be available.
        </li>
      </ol>
      {!compact && (
        <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          push notifications on iOS are throttled by 1–2 minutes — that&apos;s an Apple thing, not us.
        </p>
      )}
    </div>
  );
}
